import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import Logo from "../components/Logo";
import ThemeToggle from "../components/ThemeToggle";
import { Lock, LogOut, Search, Download, Users, RefreshCcw, Loader2, ArrowLeft } from "lucide-react";
import { toast } from "../hooks/use-toast";
import { useAdminSession } from "../hooks/use-admin-session";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminLeads = () => {
  const { authenticated, checkingSession, authLoading, signIn, signOut, adminSessionConfig } = useAdminSession();
  const [inputToken, setInputToken] = useState("");
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");

  const login = async (e) => {
    e.preventDefault();
    const ok = await signIn(inputToken);
    if (ok) {
      setInputToken("");
      toast({ title: "Signed in", description: "Loading leads…" });
    }
  };

  const logout = useCallback(async () => {
    await signOut();
    setInputToken("");
    setLeads([]);
  }, [signOut]);

  const fetchLeads = useCallback(async () => {
    if (!authenticated) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/leads`, adminSessionConfig);
      setLeads(res.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        await logout();
        toast({ title: "Session expired", description: "Please sign in again." });
      } else {
        toast({ title: "Failed to load", description: "Could not fetch leads." });
      }
    } finally {
      setLoading(false);
    }
  }, [adminSessionConfig, authenticated, logout]);

  useEffect(() => {
    if (authenticated) fetchLeads();
  }, [authenticated, fetchLeads]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return leads;
    return leads.filter((l) =>
      [l.name, l.email, l.organization, l.phone, l.message, l.source]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(query))
    );
  }, [leads, q]);

  const exportCsv = () => {
    const rows = filtered;
    if (rows.length === 0) {
      toast({ title: "Nothing to export", description: "No leads match your search." });
      return;
    }
    const headers = ["created_at", "name", "email", "organization", "phone", "source", "message"];
    const escape = (v) => {
      if (v === null || v === undefined) return "";
      const s = String(v).replace(/"/g, '""');
      return `"${s}"`;
    };
    const csv = [headers.join(","), ...rows.map((r) => headers.map((h) => escape(r[h])).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `v4-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (checkingSession) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F7FB] text-[#0B1B3B]">
        <div className="inline-flex items-center gap-3 text-sm font-semibold"><Loader2 size={18} className="animate-spin" /> Checking admin session…</div>
      </div>
    );
  }

  if (!authenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F7FB] p-6">
        <form onSubmit={login} className="w-full max-w-md bg-white rounded-2xl border border-slate-200 p-8 shadow-xl">
          <div className="flex items-center justify-between mb-6">
            <Logo variant="light" size={40} />
            <ThemeToggle />
          </div>
          <div className="h-12 w-12 rounded-xl bg-[#0B1B3B] text-white flex items-center justify-center">
            <Lock size={20} />
          </div>
          <h1 className="mt-4 text-2xl font-bold text-[#0B1B3B]">Admin sign-in</h1>
          <p className="mt-2 text-sm text-slate-600">Enter your admin token to start a secure cookie session for captured leads.</p>

          <label className="block mt-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Admin Token</label>
          <input
            type="password"
            value={inputToken}
            onChange={(e) => setInputToken(e.target.value)}
            placeholder="Paste your token…"
            className="mt-2 w-full border border-slate-200 rounded-lg px-4 py-3 outline-none focus:border-[#1E56E3] text-sm"
          />
          <button type="submit" disabled={authLoading} className="btn-primary mt-5 w-full justify-center disabled:opacity-70">
            {authLoading ? (<><Loader2 size={16} className="animate-spin" /> Verifying…</>) : ("Sign In")}
          </button>

          <div className="mt-6 text-center">
            <Link to="/" className="text-xs text-slate-500 hover:text-[#1E56E3] inline-flex items-center gap-1">
              <ArrowLeft size={12} /> Back to site
            </Link>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F7FB]">
      {/* Top bar */}
      <div className="bg-white border-b border-slate-200">
        <div className="container-x flex items-center justify-between h-16">
          <div className="flex items-center gap-4">
            <Logo variant="light" size={36} />
            <span className="hidden md:inline text-xs font-semibold uppercase tracking-wider text-slate-500 border border-slate-200 rounded-full px-3 py-1">
              Admin
            </span>
          </div>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <button onClick={logout} className="inline-flex items-center gap-2 text-sm font-semibold text-[#0B1B3B] hover:text-[#1E56E3] px-3 py-2">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </div>

      <div className="container-x py-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <span className="tag-chip">Leads Inbox</span>
            <h1 className="section-title mt-3">Captured inquiries</h1>
            <p className="text-sm text-slate-500 mt-2">All submissions from the /contact form and other sources.</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={fetchLeads} className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2.5 rounded-full border border-slate-200 hover:border-[#1E56E3] hover:text-[#1E56E3]">
              <RefreshCcw size={14} /> Refresh
            </button>
            <button onClick={exportCsv} className="btn-primary">
              <Download size={14} /> Export CSV
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="mt-8 grid sm:grid-cols-3 gap-4">
          <div className="rounded-2xl bg-white border border-slate-200 p-6">
            <div className="flex items-center gap-2 text-[#1E56E3]"><Users size={16} /><span className="text-xs font-semibold uppercase tracking-wider">Total Leads</span></div>
            <div className="mt-3 text-4xl font-extrabold text-[#0B1B3B]">{leads.length}</div>
          </div>
          <div className="rounded-2xl bg-white border border-slate-200 p-6">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Matching Filter</div>
            <div className="mt-3 text-4xl font-extrabold text-[#0B1B3B]">{filtered.length}</div>
          </div>
          <div className="rounded-2xl bg-white border border-slate-200 p-6">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Latest</div>
            <div className="mt-3 text-sm font-semibold text-[#0B1B3B]">
              {leads[0] ? new Date(leads[0].created_at).toLocaleString() : "—"}
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="mt-8 relative max-w-xl">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, email, org, message…"
            className="w-full pl-11 pr-4 py-3 rounded-full border border-slate-200 bg-white text-sm outline-none focus:border-[#1E56E3]"
          />
        </div>

        {/* Table */}
        <div className="mt-6 rounded-2xl bg-white border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F5F7FB]">
                <tr>
                  <th className="text-left px-5 py-4 text-[11px] font-bold uppercase tracking-wider text-slate-500">When</th>
                  <th className="text-left px-5 py-4 text-[11px] font-bold uppercase tracking-wider text-slate-500">Name</th>
                  <th className="text-left px-5 py-4 text-[11px] font-bold uppercase tracking-wider text-slate-500">Email</th>
                  <th className="text-left px-5 py-4 text-[11px] font-bold uppercase tracking-wider text-slate-500">Organization</th>
                  <th className="text-left px-5 py-4 text-[11px] font-bold uppercase tracking-wider text-slate-500">Phone</th>
                  <th className="text-left px-5 py-4 text-[11px] font-bold uppercase tracking-wider text-slate-500">Message</th>
                  <th className="text-left px-5 py-4 text-[11px] font-bold uppercase tracking-wider text-slate-500">Source</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={7} className="px-5 py-10 text-center text-slate-500"><Loader2 size={16} className="animate-spin inline mr-2" /> Loading…</td></tr>
                )}
                {!loading && filtered.length === 0 && (
                  <tr><td colSpan={7} className="px-5 py-10 text-center text-slate-500">No leads yet.</td></tr>
                )}
                {!loading && filtered.map((l) => (
                  <tr key={l.id} className="border-t border-slate-100 hover:bg-[#FAFBFF]">
                    <td className="px-5 py-4 text-slate-500 whitespace-nowrap">{new Date(l.created_at).toLocaleString()}</td>
                    <td className="px-5 py-4 font-semibold text-[#0B1B3B]">{l.name}</td>
                    <td className="px-5 py-4"><a href={`mailto:${l.email}`} className="text-[#1E56E3] hover:underline">{l.email}</a></td>
                    <td className="px-5 py-4 text-slate-700">{l.organization || "—"}</td>
                    <td className="px-5 py-4 text-slate-700 whitespace-nowrap">{l.phone || "—"}</td>
                    <td className="px-5 py-4 text-slate-700 max-w-[320px]">
                      <div className="line-clamp-2">{l.message || "—"}</div>
                    </td>
                    <td className="px-5 py-4">
                      <span className="text-[10px] font-bold uppercase tracking-wider bg-[#EEF3FF] text-[#1E56E3] px-2 py-1 rounded">{l.source || "contact_form"}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLeads;
